import React, { useState, useEffect, useRef } from 'react';
import './BusinessInfoForm.css';
import PasoPlegable from './business/PasoPlegable';
import PieDePaso from './business/PieDePaso';
import { API_URL } from '../config/api';

// Función para generar slug desde el nombre del negocio
const generateSlug = (name) => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[áàäâ]/g, 'a')
    .replace(/[éèëê]/g, 'e')
    .replace(/[íìïî]/g, 'i')
    .replace(/[óòöô]/g, 'o')
    .replace(/[úùüû]/g, 'u')
    .replace(/[ñ]/g, 'n')
    .replace(/[ç]/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '') // Remover caracteres especiales
    .replace(/\s+/g, '-') // Reemplazar espacios con guiones
    .replace(/-+/g, '-') // Remover guiones múltiples
    .replace(/^-|-$/g, ''); // Remover guiones al inicio y final
};

// Función para generar dirección completa desde los campos individuales
const generateFullAddress = (addressFields) => {
  const parts = [];
  
  if (addressFields?.street) {
    parts.push(addressFields.street);
  }
  
  if (addressFields?.floor) {
    parts.push(addressFields.floor);
  }
  
  if (addressFields?.city) {
    parts.push(addressFields.city);
  }
  
  if (addressFields?.province) {
    parts.push(addressFields.province);
  }
  
  if (addressFields?.postalCode) {
    parts.push(`CP ${addressFields.postalCode}`);
  }
  
  if (addressFields?.country && addressFields.country !== 'Argentina') {
    parts.push(addressFields.country);
  }
  
  return parts.filter(Boolean).join(', ');
};

// Función para validar slug con el servidor
const validateSlugAvailability = async (slug) => {
  if (!slug || slug.length < 3) {
    return { available: false, message: 'El slug debe tener al menos 3 caracteres' };
  }
  
  try {
    const apiUrl = `${API_URL}/api/check-slug/${slug}`;

    
    // Intentar llamar al backend para verificar disponibilidad
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    
    
    if (response.ok) {
      const result = await response.json();
      
      return {
        available: result.available,
        message: result.available ? 'URL disponible' : 'Esta URL ya está ocupada. Probá con otra.'
      };
    } else {
      // Si el backend no responde, usar validación local
      console.error(`❌ FRONTEND: Error HTTP ${response.status}`);
      throw new Error(`Backend error: ${response.status}`);
    }
  } catch (error) {
    console.warn('❌ FRONTEND: Error al verificar slug con backend, usando validación local:', error);
    
    // Validación local de fallback
    const unavailableNames = ['admin', 'api', 'www', 'mail', 'ftp', 'test', 'demo', 'peluqueria-centro', 'spa-relax', 'aturno', 'app', 'dashboard', 'login', 'register', 'booking', 'calendar', 'settings'];
    
    if (unavailableNames.includes(slug)) {
      return { available: false, message: 'Esta URL ya está ocupada. Probá con otra.' };
    }
    
    return { available: true, message: 'URL disponible (verificación local)' };
  }
};

// Placeholders dinámicos por tipo de negocio
const getPlaceholders = (businessType) => {
  const placeholders = {
    beauty: {
      name: 'Ej: Salón María Estética',
      email: 'contacto@salonmaria.com',
      address: 'Av. Corrientes 1234, CABA',
      phone: '+54 9 11 1234-5678'
    },
    health: {
      name: 'Ej: Consultorio Dr. Pérez',
      email: 'consultas@drperez.com',
      address: 'Av. Santa Fe 2500, CABA',
      phone: '+54 9 11 2345-6789'
    },
    fitness: {
      name: 'Ej: Gimnasio FitZone',
      email: 'info@fitzone.com',
      address: 'Av. Cabildo 1500, CABA',
      phone: '+54 9 11 3456-7890'
    },
    sports: {
      name: 'Ej: Club Deportivo Los Pinos',
      email: 'reservas@lospinos.com',
      address: 'Av. del Libertador 3000, CABA',
      phone: '+54 9 11 4567-8901'
    },
    food: {
      name: 'Ej: Restaurante La Cocina',
      email: 'reservas@lacocina.com',
      address: 'Av. Corrientes 800, CABA',
      phone: '+54 9 11 5678-9012'
    },
    education: {
      name: 'Ej: Academia de Idiomas',
      email: 'info@academiaidiomas.com',
      address: 'Av. Rivadavia 1200, CABA',
      phone: '+54 9 11 6789-0123'
    },
    services: {
      name: 'Ej: Consultora Estratégica',
      email: 'contacto@consultora.com',
      address: 'Av. 9 de Julio 1000, CABA',
      phone: '+54 9 11 7890-1234'
    },
    other: {
      name: 'Ej: Mi Negocio',
      email: 'contacto@minegocio.com',
      address: 'Tu dirección comercial',
      phone: '+54 9 11 1234-5678'
    }
  };

  return placeholders[businessType] || placeholders.other;
};

/**
 * Los datos básicos del negocio, en el mismo acordeón que el detalle de un
 * servicio (EditableServiceCard): una parte por vez, con el botón al pie que la
 * cierra y abre la siguiente.
 *
 * `onSiguiente` es lo que hace ese botón en la ÚLTIMA parte, y por eso lo pone
 * el padre: en el onboarding es pasar al paso de horarios, y en el panel no es
 * nada —ahí este formulario ya está adentro de otro plegable y la pantalla
 * tiene su propio "Guardar cambios"—, así que sin la prop la última parte
 * simplemente no lleva pie. Los otros tres nombres son los de PasoNavegador, que
 * es el otro lugar que decide si se puede avanzar y por qué no.
 */
export default function BusinessInfoForm({
  value,
  onChange,
  businessType = 'other',
  isExistingBusiness = false,
  onSiguiente,
  textoSiguiente = 'Continuar',
  puedeAvanzar = true,
  motivoBloqueo = '',
}) {
  const [slugValidation, setSlugValidation] = useState({ status: 'idle', message: '' });
  const [isValidatingSlug, setIsValidatingSlug] = useState(false);
  const nameDebounceRef = useRef(null);
  const slugDebounceRef = useRef(null);
  const lastGeneratedNameRef = useRef('');
  const [isManuallyEdited, setIsManuallyEdited] = useState(false);
  // Qué paso del acordeón está abierto. Arranca en el primero; abrir otro cierra
  // el anterior, y `null` deja todo plegado.
  const [pasoAbierto, setPasoAbierto] = useState(1);
  
  // Estado para controlar auto-generación vs edición manual

  // Acá estaba generateCompleteAddress, que armaba la dirección al apretar el
  // botón "Generar dirección completa". Se fue con el botón: la dirección se
  // arma sola en el efecto de más abajo, que es lo que consume la página
  // pública.

  // Obtener placeholders dinámicos
  const placeholders = getPlaceholders(businessType);

  // Auto-generar dirección completa cuando cambian los campos de dirección
  useEffect(() => {
    const fullAddress = generateFullAddress(value);
    if (fullAddress && fullAddress !== value?.address) {
      onChange({ ...value, address: fullAddress });
    }
  }, [value?.street, value?.floor, value?.city, value?.province, value?.postalCode, value?.country]);

  // Auto-generar slug cuando cambia el nombre del negocio (con debounce)
  useEffect(() => {
    // No auto-generar si es un negocio existente
    if (isExistingBusiness) return;
    
    // No auto-generar si el usuario editó manualmente la URL
    if (isManuallyEdited) return;
    
    // No auto-generar si no hay nombre
    if (!value?.name) return;
    
    // Limpiar timeout anterior
    if (nameDebounceRef.current) {
      clearTimeout(nameDebounceRef.current);
    }

    // Crear nuevo timeout para generar el slug
    nameDebounceRef.current = setTimeout(() => {
      const generatedSlug = generateSlug(value.name);
      lastGeneratedNameRef.current = value.name;
      
      // Solo auto-generar si no fue editado manualmente
      if (generatedSlug && !isManuallyEdited) {
        console.log(`🔄 AUTO-GENERANDO URL desde nombre: "${value.name}" → "${generatedSlug}"`);
        onChange({ ...value, slug: generatedSlug });
      }
    }, 300); // 300ms debounce para que sea más responsivo

    // Cleanup
    return () => {
      if (nameDebounceRef.current) {
        clearTimeout(nameDebounceRef.current);
      }
    };
  }, [value?.name, onChange, isManuallyEdited, isExistingBusiness]);

  // Validar slug con debounce
  useEffect(() => {
    // No validar si es un negocio existente
    if (isExistingBusiness) {
      setSlugValidation({ status: 'idle', message: '' });
      return;
    }
    
    if (!value?.slug || value.slug.length === 0) {
      setSlugValidation({ status: 'idle', message: '' });
      return;
    }

    // Limpiar timeout anterior
    if (slugDebounceRef.current) {
      clearTimeout(slugDebounceRef.current);
    }

    // No mostrar "Verificando..." inmediatamente para evitar parpadeos
    const immediateCheck = setTimeout(() => {
      if (value.slug.length >= 3) {
        setSlugValidation({ status: 'validating', message: 'Verificando disponibilidad...' });
        setIsValidatingSlug(true);
      }
    }, 200);

    // Crear nuevo timeout para validación
    slugDebounceRef.current = setTimeout(async () => {
      if (value.slug.length < 3) {
        setSlugValidation({ 
          status: 'unavailable', 
          message: 'El slug debe tener al menos 3 caracteres' 
        });
        setIsValidatingSlug(false);
        return;
      }

      try {

        const result = await validateSlugAvailability(value.slug);
        
        setSlugValidation({
          status: result.available ? 'available' : 'unavailable',
          message: result.message
        });
        
      } catch (error) {
        console.error(`❌ FRONTEND: Error en validación:`, error);
        setSlugValidation({
          status: 'error',
          message: 'Error al verificar disponibilidad'
        });
      } finally {
        setIsValidatingSlug(false);
      }
    }, 800); // 800ms debounce para validación

    // Cleanup
    return () => {
      clearTimeout(immediateCheck);
      if (slugDebounceRef.current) {
        clearTimeout(slugDebounceRef.current);
      }
    };
  }, [value?.slug, isExistingBusiness]);

  const handleSlugChange = (e) => {
    const inputValue = e.target.value;
    
    // Si el usuario borra todo, permitir auto-generación nuevamente
    if (inputValue.trim() === '') {
      setIsManuallyEdited(false);
      console.log(`🔄 RESET: Campo URL vacío, reactivando auto-generación`);
      onChange({ ...value, slug: '' });
      return;
    }
    
    // Marcar que el usuario editó manualmente la URL
    setIsManuallyEdited(true);
    console.log(`✏️ EDICIÓN MANUAL: Usuario cambió URL a "${inputValue}"`);
    
    // Generar slug a partir del valor ingresado
    const newSlug = generateSlug(inputValue);
    onChange({ ...value, slug: newSlug });
  };

  const getSlugStatusIcon = () => {
    if (isValidatingSlug) return '⏳';
    if (slugValidation.status === 'available') return '✅';
    if (slugValidation.status === 'unavailable') return '❌';
    if (slugValidation.status === 'error') return '⚠️';
    return '';
  };

  const getSlugStatusClass = () => {
    if (slugValidation.status === 'available') return 'slug-available';
    if (slugValidation.status === 'unavailable') return 'slug-unavailable';
    if (slugValidation.status === 'error') return 'slug-error';
    return '';
  };

  // Resúmenes que se ven cuando el paso está cerrado, para saber qué falta sin
  // tener que abrirlo.
  const resumenDireccion = [value?.street, value?.city].filter(Boolean).join(', ');
  const resumenContacto = [value?.email, value?.phone].filter(Boolean).join(' · ');

  const paso = (numero) => ({
    numero,
    abierto: pasoAbierto === numero,
    // Acordeón: abrir uno cierra el que estaba. Volver a tocar el abierto lo
    // cierra, así se puede dejar todo plegado para revisar.
    onToggle: () => setPasoAbierto((actual) => (actual === numero ? null : numero)),
  });

  // Cuántas partes tiene el formulario. Se usa para saber cuál es la última, que
  // es la que lleva el botón distinto.
  const PARTES = 4;

  /**
   * Pie de cada parte: el botón que la cierra y abre la siguiente.
   *
   * Es el mismo de EditableServiceCard, y va por la misma razón: sin él las
   * partes se abren solo tocando su título, que es un gesto que hay que
   * descubrir, y nada dice que hay un orden ni cuándo terminaste con una.
   */
  const pie = (numero) => {
    if (numero < PARTES) {
      return <PieDePaso onClick={() => setPasoAbierto(numero + 1)} />;
    }
    // La última: lo que sigue ya no es asunto de este formulario.
    if (!onSiguiente) return null;
    return (
      <PieDePaso
        texto={textoSiguiente}
        onClick={onSiguiente}
        puedeAvanzar={puedeAvanzar}
        motivoBloqueo={motivoBloqueo}
      />
    );
  };

  return (
    <div className="business-info-form flex flex-col gap-3">
      <PasoPlegable
        {...paso(1)}
        titulo="Nombre y dirección web"
        descripcion="Cómo se llama tu negocio y en qué link lo van a encontrar tus clientes."
        resumen={value?.name || 'Sin completar'}
        completo={Boolean(value?.name && value?.slug)}
      >
        <div className="form-group">
          <label htmlFor="business-name">Nombre del negocio</label>
          <input
            id="business-name"
            type="text"
            value={value?.name || ''}
            onChange={e => onChange({ ...value, name: e.target.value })}
            placeholder={placeholders.name}
          />
        </div>

        <div className="form-group">
          <label htmlFor="business-slug">Dirección de tu página</label>
          <div className={`url-input ${getSlugStatusClass()} ${isExistingBusiness ? 'disabled' : ''}`}>
            <span className="url-prefix">aturno.com/</span>
            <input
              id="business-slug"
              type="text"
              value={value?.slug || ''}
              onChange={handleSlugChange}
              placeholder="mi-negocio"
              title={isExistingBusiness ? "La URL no se puede modificar después del registro" : "Ingresa tu URL personalizada"}
              disabled={isExistingBusiness}
            />
            <span className="slug-status-icon">{isExistingBusiness ? '🔒' : getSlugStatusIcon()}</span>
          </div>
          {slugValidation.message && !isExistingBusiness && (
            <div className={`slug-validation-message ${slugValidation.status}`}>
              {slugValidation.message}
              {slugValidation.status === 'unavailable' && (
                <small style={{display: 'block', marginTop: '0.25rem', fontStyle: 'italic'}}>
                  💡 Podés editar esta dirección para que sea única
                </small>
              )}
            </div>
          )}
          {isExistingBusiness && (
            <small className="field-hint">🔒 La dirección no se puede cambiar después del registro</small>
          )}
          {isManuallyEdited && !isExistingBusiness && (
            <small className="field-hint">✏️ La editaste a mano, así que ya no sigue al nombre</small>
          )}
        </div>
        {pie(1)}
      </PasoPlegable>

      <PasoPlegable
        {...paso(2)}
        titulo="Dirección"
        descripcion="Dónde atendés. Aparece en tu página para que tus clientes te encuentren."
        resumen={resumenDireccion || 'Sin completar'}
        completo={Boolean(value?.street && value?.city)}
      >
        {/* Se quitó el campo de "dirección completa generada automáticamente" y
            su botón: mostraban armada la misma dirección que la persona acababa
            de escribir campo por campo, y encima el botón podía quedar
            deshabilitado sin explicar por qué. La dirección se sigue armando
            sola en segundo plano, que es lo que consume la página pública. */}
        <div className="form-group">
          <label htmlFor="business-street">Calle y número</label>
          <input
            id="business-street"
            type="text"
            value={value?.street || ''}
            onChange={e => onChange({ ...value, street: e.target.value })}
            placeholder="Ej: Av. Corrientes 1234"
          />
        </div>

        <div className="form-group">
          <label htmlFor="business-floor">Piso o local <span className="campo-opcional">(opcional)</span></label>
          <input
            id="business-floor"
            type="text"
            value={value?.floor || ''}
            onChange={e => onChange({ ...value, floor: e.target.value })}
            placeholder="Ej: Piso 2, Oficina 5"
          />
        </div>

        <div className="campos-en-fila">
          <div className="form-group">
            <label htmlFor="business-city">Ciudad</label>
            <input
              id="business-city"
              type="text"
              value={value?.city || ''}
              onChange={e => onChange({ ...value, city: e.target.value })}
              placeholder="Ej: Buenos Aires"
            />
          </div>
          <div className="form-group">
            <label htmlFor="business-postal-code">Código postal <span className="campo-opcional">(opcional)</span></label>
            <input
              id="business-postal-code"
              type="text"
              value={value?.postalCode || ''}
              onChange={e => onChange({ ...value, postalCode: e.target.value })}
              placeholder="Ej: C1043"
            />
          </div>
        </div>

        <div className="campos-en-fila">
          <div className="form-group">
            <label htmlFor="business-province">Provincia</label>
            <input
              id="business-province"
              type="text"
              value={value?.province || ''}
              onChange={e => onChange({ ...value, province: e.target.value })}
              placeholder="Ej: CABA"
            />
          </div>
          <div className="form-group">
            <label htmlFor="business-country">País <span className="campo-opcional">(opcional)</span></label>
            <input
              id="business-country"
              type="text"
              value={value?.country || 'Argentina'}
              onChange={e => onChange({ ...value, country: e.target.value })}
              placeholder="Argentina"
            />
          </div>
        </div>
        {pie(2)}
      </PasoPlegable>

      <PasoPlegable
        {...paso(3)}
        titulo="Contacto"
        descripcion="Por dónde te escriben tus clientes y a dónde te avisamos de cada reserva."
        resumen={resumenContacto || 'Sin completar'}
        completo={Boolean(value?.email)}
      >
        <div className="form-group">
          <label htmlFor="business-email">Email</label>
          <input
            id="business-email"
            type="email"
            value={value?.email || ''}
            onChange={e => onChange({ ...value, email: e.target.value })}
            placeholder={placeholders.email}
          />
          <small className="field-hint">Acá te avisamos cada vez que alguien reserva un turno.</small>
        </div>

        <div className="form-group">
          <label htmlFor="business-phone">WhatsApp <span className="campo-opcional">(opcional)</span></label>
          <input
            id="business-phone"
            type="tel"
            value={value?.phone || ''}
            onChange={e => onChange({ ...value, phone: e.target.value })}
            placeholder={placeholders.phone}
          />
        </div>
        {pie(3)}
      </PasoPlegable>

      <PasoPlegable
        {...paso(4)}
        titulo={<>Mensaje para tus clientes <span className="campo-opcional">(opcional)</span></>}
        descripcion="Un aviso que se muestra arriba de todo en tu página."
        resumen={value?.mensaje ? value.mensaje : 'Sin mensaje'}
        completo={Boolean(value?.mensaje)}
      >
        <div className="form-group">
          <textarea
            id="business-mensaje"
            rows={3}
            maxLength={500}
            value={value?.mensaje || ''}
            onChange={e => onChange({ ...value, mensaje: e.target.value })}
            placeholder="Ej: Atendemos con turno previo. Si llegás más de 15 minutos tarde, el turno se reprograma."
          />
          <small className="field-hint">
            Sirve para avisos, promociones o cómo llegar. Si lo dejás vacío, en tu página no
            aparece nada: ni el recuadro ni un espacio en blanco.
          </small>
        </div>
        {pie(4)}
      </PasoPlegable>
    </div>
  );
} 